// src/pages/admin/board/BoardManage.tsx

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface Board {
  id: number
  slug: string
  name: string
  theme: 'card' | 'table' | 'gallery'
  has_file: 0 | 1
  has_image: 0 | 1
}

interface BoardForm {
  slug: string
  name: string
  theme: string
  has_file: number
  has_image: number
}

export default function BoardManage() {
  const navigate = useNavigate()
  const [boards, setBoards] = useState<Board[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingBoard, setEditingBoard] = useState<Board | null>(null)
  const [form, setForm] = useState<BoardForm>({
    slug: '',
    name: '',
    theme: 'card',
    has_file: 0,
    has_image: 1,
  })

  useEffect(() => {
    fetchBoards()
  }, [])

  const fetchBoards = async () => {
    setLoading(true)
    try {
      const res = await fetchWithAuth(getApiUrl('/marubit/api/board'))
      if (!res.ok) throw new Error('조회 실패')
      setBoards(await res.json())
    } catch (err) {
      console.error(err)
      alert('게시판 목록을 불러오지 못했습니다')
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (board?: Board) => {
    if (board) {
      setEditingBoard(board)
      setForm({
        slug: board.slug,
        name: board.name,
        theme: board.theme,
        has_file: board.has_file,
        has_image: board.has_image,
      })
    } else {
      setEditingBoard(null)
      setForm({
        slug: '',
        name: '',
        theme: 'card',
        has_file: 0,
        has_image: 1,
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingBoard(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.name.trim()) {
      alert('게시판 이름을 입력하세요')
      return
    }

    if (!editingBoard && !form.slug.trim()) {
      alert('slug를 입력하세요')
      return
    }

    if (!editingBoard && !/^[a-z0-9-_]+$/.test(form.slug)) {
      alert('slug는 영문 소문자, 숫자, -, _만 사용 가능합니다')
      return
    }

    try {
      if (editingBoard) {
        // 수정
        const res = await fetchWithAuth(
          getApiUrl(`/marubit/api/board/${editingBoard.slug}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: form.name,
              theme: form.theme,
              has_file: form.has_file,
              has_image: form.has_image,
            }),
          }
        )
        if (!res.ok) throw new Error('수정 실패')
        alert('게시판이 수정되었습니다')
      } else {
        // 생성
        const res = await fetchWithAuth(getApiUrl('/marubit/api/board'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        })
        if (!res.ok) {
          const error = await res.json()
          throw new Error(error.detail || '생성 실패')
        }
        alert('게시판이 생성되었습니다')
      }
      handleCloseModal()
      fetchBoards()
    } catch (err: any) {
      alert(err.message || '저장에 실패했습니다')
    }
  }

  const handleDelete = async (board: Board) => {
    if (!window.confirm(`"${board.name}" 게시판을 삭제하시겠습니까?`)) return

    try {
      const res = await fetchWithAuth(
        getApiUrl(`/marubit/api/board/${board.slug}`),
        { method: 'DELETE' }
      )
      if (!res.ok) {
        if (res.status === 409) {
          alert('게시글이 존재하여 삭제할 수 없습니다')
        } else {
          throw new Error('삭제 실패')
        }
        return
      }
      alert('게시판이 삭제되었습니다')
      fetchBoards()
    } catch (err) {
      alert('삭제에 실패했습니다')
    }
  }

  const getThemeLabel = (theme: string) => {
    const map: Record<string, string> = {
      card: '카드형',
      table: '테이블형',
      gallery: '갤러리형',
    }
    return map[theme] || theme
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdf8f0]">
        <p className="text-gray-400">불러오는 중...</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#f5f2f0] p-6">
      <div className="max-w-[1200px] mx-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-[24px] font-bold text-[#2a1f1a]">게시판 관리</h1>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] transition-colors text-[14px]"
          >
            + 새 게시판
          </button>
        </div>

        {/* 게시판 목록 */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <table className="w-full">
          <thead>
              <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">
                  ID
                </th>
                <th className="hidden min-[780px]:table-cell text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium">
                  Slug
                </th>
                <th className="text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium">
                  이름
                </th>
                <th className="hidden lg:table-cell text-left px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">
                  테마
                </th>
                <th className="hidden xl:table-cell text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">
                  설정
                </th>
                <th className="text-center px-5 py-3 text-[12px] text-[#5a3d32] font-medium w-[200px]">
                  관리
                </th>
              </tr>
            </thead>
            <tbody>
              {boards.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-400 text-[14px]">
                    등록된 게시판이 없습니다
                  </td>
                </tr>
              ) : (
                boards.map((board) => (
                  <tr
                    key={board.id}
                    className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4 text-[13px] text-[#333]">{board.id}</td>
                    <td className="hidden min-[780px]:table-cell px-5 py-4 text-[13px] text-[#887267] font-mono">
                      {board.slug}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-[#333] font-medium">
                      {board.name}
                    </td>
                    <td className="hidden lg:table-cell px-5 py-4 text-[13px] text-[#666]">
                      {getThemeLabel(board.theme)}
                    </td>
                    <td className="hidden xl:table-cell px-5 py-4 text-center">
                      <div className="flex gap-1 justify-center">
                        {board.has_file === 1 && (
                          <span className="text-[18px]" title="파일 첨부 가능">📎</span>
                        )}
                        {board.has_image === 1 && (
                          <span className="text-[18px]" title="이미지 사용">🖼️</span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() =>
                            navigate(`/admin/board/${board.slug}/post`)
                          }
                          className="px-3 py-1 text-[12px] bg-[#887267] text-white rounded hover:bg-[#6b5248] transition-colors whitespace-nowrap"
                        >
                          게시글 관리
                        </button>
                        <button
                          onClick={() => handleOpenModal(board)}
                          className="px-3 py-1 text-[12px] border border-[#887267] text-[#887267] rounded hover:bg-[#f5f2f0] transition-colors whitespace-nowrap"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(board)}
                          className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded hover:bg-red-50 transition-colors whitespace-nowrap"
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 모달 */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[500px] mx-4">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-[20px] font-bold text-[#2a1f1a]">
                {editingBoard ? '게시판 수정' : '새 게시판'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {/* Slug */}
              <div className="mb-4">
                <label className="block text-[12px] text-gray-400 mb-1">
                  Slug {!editingBoard && <span className="text-red-400">*</span>}
                </label>
                <input
                  type="text"
                  className={`w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none ${
                    editingBoard
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'text-[#2a1f1a] focus:border-[#887267]'
                  }`}
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  disabled={!!editingBoard}
                  placeholder="news, gallery, notice..."
                />
                {!editingBoard && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    영문 소문자, 숫자, -, _ 만 사용 가능 (생성 후 변경 불가)
                  </p>
                )}
              </div>

              {/* 이름 */}
              <div className="mb-4">
                <label className="block text-[12px] text-gray-400 mb-1">
                  이름 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267]"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="소식, 갤러리, 공지사항..."
                />
              </div>

              {/* 테마 */}
              <div className="mb-4">
                <label className="block text-[12px] text-gray-400 mb-1">테마</label>
                <select
                  className="w-full px-3 py-2 text-[13px] border border-[#e8e0dc] rounded-lg outline-none text-[#2a1f1a] focus:border-[#887267] bg-white"
                  value={form.theme}
                  onChange={(e) => setForm({ ...form, theme: e.target.value })}
                >
                  <option value="card">카드형</option>
                  <option value="table">테이블형</option>
                  <option value="gallery">갤러리형</option>
                </select>
              </div>

              {/* 설정 */}
              <div className="mb-6">
                <label className="block text-[12px] text-gray-400 mb-2">설정</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.has_file === 1}
                      onChange={(e) =>
                        setForm({ ...form, has_file: e.target.checked ? 1 : 0 })
                      }
                      className="w-4 h-4 accent-[#887267]"
                    />
                    <span className="text-[13px] text-[#333]">
                      파일 첨부 기능 사용
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.has_image === 1}
                      onChange={(e) =>
                        setForm({ ...form, has_image: e.target.checked ? 1 : 0 })
                      }
                      className="w-4 h-4 accent-[#887267]"
                    />
                    <span className="text-[13px] text-[#333]">
                      이미지 사용 (대표 이미지)
                    </span>
                  </label>
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors text-[14px]"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] transition-colors text-[14px]"
                >
                  {editingBoard ? '수정' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}